import { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/shared/components/ui/toaster";
import { Toaster as Sonner } from "@/shared/components/ui/sonner";
import { TooltipProvider } from "@/shared/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { AzureAuthProvider, KeycloakAuthProvider, SentryUserSync } from "@/features/auth";
import { ProcessingProvider } from "@/shared/contexts/ProcessingContext";
import { ProtectedRoute } from "@/shared/components/ProtectedRoute";
import { AuthCallback } from "@/shared/components/AuthCallback";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { AppLayout } from "@/shared/components/AppLayout";
import { PageLoader, SectionLoader } from "@/shared/components/PageLoader";
import { UploadProvider } from "@/shared/contexts/UploadContext";
import { StreamingProvider } from "@/shared/contexts/StreamingContext";
import { UploadIndicator } from "@/shared/components/UploadIndicator";
import { StreamingIndicator } from "@/shared/components/StreamingIndicator";
import { isPrrMode } from "@/prr";

const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const NewProject = lazy(() => import("./pages/NewProject"));
const ProjectView = lazy(() => import("./pages/ProjectView"));
const SessionView = lazy(() => import("./pages/SessionView"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      retryDelay: 1000,
    },
  },
});

const getAuthProviderType = (): "azure" | "keycloak" => {
  const azureClientId = import.meta.env.VITE_AZURE_CLIENT_ID;
  const azureTenantId = import.meta.env.VITE_AZURE_TENANT_ID;
  const azureAuthority = import.meta.env.VITE_AZURE_AUTHORITY;

  const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL;
  const keycloakRealm = import.meta.env.VITE_KEYCLOAK_REALM;
  const keycloakClientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;

  if (azureClientId || azureTenantId || azureAuthority) {
    return "azure";
  }

  if (keycloakUrl && keycloakRealm && keycloakClientId) {
    return "keycloak";
  }

  return "keycloak";
};

const CurrentAuthProvider =
  getAuthProviderType() === "azure" ? AzureAuthProvider : KeycloakAuthProvider;

function ProtectedAppRoute({
  children,
  section,
}: {
  children: React.ReactNode;
  section: string;
}) {
  const { t } = useTranslation("common");

  return (
    <ProtectedRoute>
      <AppLayout>
        <ErrorBoundary section={section}>
          <Suspense fallback={<SectionLoader message={t("loading")} />}>
            {children}
          </Suspense>
        </ErrorBoundary>
      </AppLayout>
    </ProtectedRoute>
  );
}

function LegacySessionRedirect() {
  const { sessionId } = useParams();

  if (!sessionId) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to={`/dashboard/session/${sessionId}`} replace />;
}

const App = () => {
  const { t: tCommon } = useTranslation("common");
  const { t: tProject } = useTranslation("project");
  const { t: tAdmin } = useTranslation("admin");

  return (
    <ErrorBoundary section={tCommon("application")}>
      <QueryClientProvider client={queryClient}>
        <CurrentAuthProvider>
          <SentryUserSync />
          <ProcessingProvider>
            <UploadProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <StreamingProvider>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <Index />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/login"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <Login />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/register"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <Register />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/access-denied"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <AccessDenied />
                          </Suspense>
                        }
                      />
                      <Route path="/callback" element={<AuthCallback />} />

                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedAppRoute section="Dashboard">
                            <Dashboard />
                          </ProtectedAppRoute>
                        }
                      />

                      {isPrrMode() ? (
                        <>
                          <Route
                            path="/dashboard/session/:sessionId"
                            element={
                              <ProtectedAppRoute section={tCommon("session")}>
                                <SessionView />
                              </ProtectedAppRoute>
                            }
                          />
                          <Route
                            path="/dashboard/new-project"
                            element={
                              <ProtectedAppRoute section="Redirecionamento">
                                <Navigate to="/dashboard" replace />
                              </ProtectedAppRoute>
                            }
                          />
                          <Route
                            path="/dashboard/project/:projectId"
                            element={
                              <ProtectedAppRoute section="Redirecionamento">
                                <Navigate to="/dashboard" replace />
                              </ProtectedAppRoute>
                            }
                          />
                          <Route
                            path="/dashboard/project/:projectId/session/:sessionId"
                            element={
                              <ProtectedAppRoute section="Redirecionamento">
                                <LegacySessionRedirect />
                              </ProtectedAppRoute>
                            }
                          />
                        </>
                      ) : (
                        <>
                          <Route
                            path="/dashboard/new-project"
                            element={
                              <ProtectedAppRoute section={tProject("newProject")}>
                                <NewProject />
                              </ProtectedAppRoute>
                            }
                          />
                          <Route
                            path="/dashboard/project/:projectId"
                            element={
                              <ProtectedAppRoute section={tCommon("project")}>
                                <ProjectView />
                              </ProtectedAppRoute>
                            }
                          />
                          <Route
                            path="/dashboard/project/:projectId/session/:sessionId"
                            element={
                              <ProtectedAppRoute section={tCommon("session")}>
                                <SessionView />
                              </ProtectedAppRoute>
                            }
                          />
                        </>
                      )}

                      <Route
                        path="/admin"
                        element={
                          <ProtectedAppRoute section={tAdmin("title")}>
                            <AdminPage />
                          </ProtectedAppRoute>
                        }
                      />

                      <Route
                        path="*"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <NotFound />
                          </Suspense>
                        }
                      />
                    </Routes>
                    <UploadIndicator />
                    <StreamingIndicator />
                  </StreamingProvider>
                </BrowserRouter>
              </TooltipProvider>
            </UploadProvider>
          </ProcessingProvider>
        </CurrentAuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
